import React, { useState, useEffect } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import i18n from 'i18n-js';

import { font } from '@styles/fonts';
import { isPhone, Popup } from '@helpers';

import { scale } from '@components/frame';

import TouchableElement from '@components/common/touchableElement';
import EditableLine from '@components/common/editableLine';
import { modalFieldBg, SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';

const Edit = ({
  C,
  updateData,
  hide,
  data = {},
  inputs,
  isLoading,
  submit,
  onClose,
  header,
  subHeader,
  rounded,
  round,
  bottomText,
  submitDisabled,
}) => {
  const disable = submitDisabled ? submitDisabled(data) : false;
  return (
    <View
      testID={TEST_IDS.clampingTool.editFieldModal}
      style={[
        isPhone
          ? {
              width: '100%',
              flex: 1,
              borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
            }
          : {
              width: '100%',
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              paddingBottom: scale(17),
            },
      ]}
    >
      <View
        style={{
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
          zIndex: 1000,
          height: scale(55),
        }}
      >
        <View>
          <TouchableElement
            onPress={() => {
              if (onClose) {
                onClose();
              }
              if (hide) {
                hide();
              }
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(15) }]}>
                <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>{i18n.t('Cancel')}</Text>
              </View>
            )}
          </TouchableElement>
        </View>
        <View>
          <TouchableElement onPress={disable ? null : submit}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(15) }]}>
                {isLoading ? (
                  <ActivityIndicator size="small" color="#777777" />
                ) : (
                  <Text style={[{ color: C(pressHoverColor(disable ? 'Label/Light/Quinary' : 'Link/Light/Primary')) }, font.Headline]}>
                    {i18n.t('Done')}
                  </Text>
                )}
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
      <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
        <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t(header)}</Text>
      </View>
      <View style={{ width: '100%' }}>
        <Text
          style={[{ color: C('Label/Light/Tertiary'), width: '100%', paddingLeft: scale(isPhone ? 15 : 30), marginBottom: scale(5) }, font.Footnote]}
        >
          {subHeader}
        </Text>
      </View>
      <View style={{ width: '100%', paddingHorizontal: scale(isPhone && !rounded ? 0 : 15) }}>
        {inputs.map((item, index) => {
          const show = item.showWhen ? item.showWhen(data) : true;
          if (!show) {
            return null;
          }
          return (
            <EditableLine
              key={item.key}
              {...{
                withoutPositionAbsolute: true,
                item: {
                  isLable: false,
                  value: data[item.key],
                  onChange: (v) => updateData(item.key, v),
                  small: isPhone,
                  ...item,
                },
                bg: modalFieldBg,
                index,
                array: inputs,
                C,
                bottomBorderBox: round ? false : isPhone ? !rounded : false,
                topBorderBox: round ? false : isPhone ? !rounded : false,
                topBorder: round ? false : index !== 0,
                roundTop: round || ((!isPhone || rounded) && index === 0),
                roundBottom: round || ((!isPhone || rounded) && index === inputs.length - 1),
              }}
            />
          );
        })}
      </View>
      {bottomText && (
        <View style={{ paddingHorizontal: scale(30), marginTop: scale(6) }}>
          <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(bottomText)}</Text>
        </View>
      )}
    </View>
  );
};

const EditBaseFrame = (props) => {
  const { C, hide, onClose, initData = {}, inputs = [], onSubmit, onChange } = props;
  const [data, setData] = useState(initData);
  const [isLoading, setLoading] = useState(false);

  const values = inputs.map((i) => initData[i.key]);

  useEffect(() => {
    setData(initData);
  }, [values.length]);

  const submit = async () => {
    if (isLoading) {
      return;
    }
    setLoading(true);
    await onSubmit(data, () => {
      if (onClose) {
        onClose();
      }
      if (hide) {
        hide();
      }
    });
    setLoading(false);
  };

  const updateData = (key, v) => {
    if (onChange) {
      onChange({ key, v, data, setData });
    } else {
      const newData = {
        ...data,
        [key]: v,
      };
      setData(newData);
    }
  };

  const itemProps = {
    ...props,
    updateData,
    data,
    inputs,
    isLoading,
    submit,
    onClose,
  };

  return <Edit {...{ ...itemProps, ...props }} />;
};

export default (props) => {
  const { showModal, isPopup = true } = props;
  useEffect(() => {
    if (!isPopup) {
      return;
    }

    if (showModal) {
      Popup({
        Component: (p) => <EditBaseFrame {...{ ...p, ...props }} />,
      });
    }
  }, [showModal]);

  return isPopup ? null : <EditBaseFrame {...props} />;
};
